import { Input, Label } from '@kmgestao/ui';
import type {
  PlatformSettingField,
  PlatformSettingsFormInput,
} from '../../../../features/platform-settings-admin/index.js';

import { CollapsibleSectionCard } from './CollapsibleSectionCard.js';

/** Seção dedicada às 14 cláusulas editáveis do contrato. Split do
 * PlatformSettingsForm principal pra não estourar o hard limit (300
 * linhas) de componente React com JSX denso.
 *
 * Renderiza 3 sub-cards:
 *   1. Cláusula 1 (Objeto) — 3 variantes por proposal_type
 *   2. Cláusula 2 (Valor) — 3 variantes por proposal_type
 *   3. Cláusulas 3-8 universais — 6 textareas
 *
 * Cada card é independente e colapsável. Alexandre abre só o que precisa
 * editar sem scroll infinito. */
export function ContractClausesSection({
  fields,
  values,
  errors,
  disabled,
  onChange,
}: {
  fields: readonly PlatformSettingField[];
  values: PlatformSettingsFormInput;
  errors: Record<string, string>;
  disabled: boolean;
  onChange: (key: string) => (value: string) => void;
}) {
  const contractFields = fields.filter((f) => f.group === 'contract');
  const objetoFields = contractFields.filter((f) => f.key.startsWith('contract.objeto_'));
  const valorFields = contractFields.filter((f) => f.key.startsWith('contract.valor_'));
  const universalFields = contractFields.filter(
    (f) => !f.key.startsWith('contract.objeto_') && !f.key.startsWith('contract.valor_'),
  );

  return (
    <>
      <CollapsibleSectionCard
        title="Contrato — Cláusula 1 (Objeto)"
        description="Texto da cláusula de objeto, com 3 variantes conforme o tipo de proposta aceita (SaaS, Consultoria ou Combo). A edge function generate-contract-pdf escolhe automaticamente a variante certa."
      >
        <ContractPlaceholdersHelp />
        <div className="space-y-5">
          {objetoFields.map((field) => (
            <ContractFieldRow
              key={field.key}
              field={field}
              value={values[field.key] ?? ''}
              error={errors[field.key]}
              onChange={onChange(field.key)}
              disabled={disabled}
            />
          ))}
        </div>
      </CollapsibleSectionCard>

      <CollapsibleSectionCard
        title="Contrato — Cláusula 2 (Valor e Pagamento)"
        description="Valor mensal (SaaS), valor total (Consultoria) ou combinado (Combo). Edite com atenção — esses textos são validados automaticamente contra os valores da proposta."
      >
        <ContractPlaceholdersHelp />
        <div className="space-y-5">
          {valorFields.map((field) => (
            <ContractFieldRow
              key={field.key}
              field={field}
              value={values[field.key] ?? ''}
              error={errors[field.key]}
              onChange={onChange(field.key)}
              disabled={disabled}
            />
          ))}
        </div>
      </CollapsibleSectionCard>

      <CollapsibleSectionCard
        title="Contrato — Cláusulas 3 a 8 (Universais)"
        description="Prazo, rescisão, LGPD, confidencialidade, foro, disposições gerais. Mesmo texto servirá os 3 tipos de contrato."
      >
        <ContractPlaceholdersHelp />
        <div className="space-y-5">
          {universalFields.map((field) => (
            <ContractFieldRow
              key={field.key}
              field={field}
              value={values[field.key] ?? ''}
              error={errors[field.key]}
              onChange={onChange(field.key)}
              disabled={disabled}
            />
          ))}
        </div>
      </CollapsibleSectionCard>
    </>
  );
}

function ContractPlaceholdersHelp() {
  return (
    <div className="mb-4 rounded-md border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
      <p className="font-semibold text-foreground">Placeholders do contrato:</p>
      <div className="mt-1 grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
        <p>
          <code className="rounded bg-background px-1">{'{razao_social}'}</code>,{' '}
          <code className="rounded bg-background px-1">{'{nome_fantasia}'}</code>,{' '}
          <code className="rounded bg-background px-1">{'{cnpj}'}</code>,{' '}
          <code className="rounded bg-background px-1">{'{endereco}'}</code>,{' '}
          <code className="rounded bg-background px-1">{'{cidade_uf}'}</code> — contratada
        </p>
        <p>
          <code className="rounded bg-background px-1">{'{lead_name}'}</code>,{' '}
          <code className="rounded bg-background px-1">{'{clinic_name}'}</code>,{' '}
          <code className="rounded bg-background px-1">{'{lead_cnpj}'}</code>,{' '}
          <code className="rounded bg-background px-1">{'{lead_email}'}</code> — contratante
        </p>
        <p>
          <code className="rounded bg-background px-1">{'{plan_name}'}</code>,{' '}
          <code className="rounded bg-background px-1">{'{monthly_price}'}</code>,{' '}
          <code className="rounded bg-background px-1">{'{billing_cycle}'}</code>,{' '}
          <code className="rounded bg-background px-1">{'{trial_days}'}</code> — SaaS
        </p>
        <p>
          <code className="rounded bg-background px-1">{'{consulting_duration_months}'}</code>,{' '}
          <code className="rounded bg-background px-1">{'{consulting_total_price}'}</code>,{' '}
          <code className="rounded bg-background px-1">{'{consulting_scope_notes}'}</code>,{' '}
          <code className="rounded bg-background px-1">{'{signing_date}'}</code> —
          consultoria/sistema
        </p>
      </div>
    </div>
  );
}

function ContractFieldRow({
  field,
  value,
  error,
  onChange,
  disabled,
}: {
  field: PlatformSettingField;
  value: string;
  error?: string | undefined;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const id = `platform-setting-${field.key}`;
  const describedBy = error ? `${id}-error` : `${id}-help`;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {field.label}
        {field.required ? <span className="ml-1 text-destructive">*</span> : null}
      </Label>
      {field.input === 'textarea' ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={6}
          placeholder={field.placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
      ) : (
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={field.placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
        />
      )}
      {error ? (
        <p id={`${id}-error`} className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : (
        <p id={`${id}-help`} className="text-xs text-muted-foreground">
          {field.helpText}
        </p>
      )}
    </div>
  );
}
